//
// Copyright 2025 DXOS.org
//

export const DX_ANCHOR_ACTIVATE = 'dx-anchor-activate';

/**
 * Attribute stamped on popover content elements so hover-driven anchors (`dx-anchor`) can tell that
 * the pointer traveled onto the open card and must not dismiss it. Shared contract between
 * `@dxos/lit-ui` (which cannot depend on React) and `@dxos/react-ui`'s Popover.
 */
export const DX_POPOVER_CONTENT_ATTR = 'data-dx-popover-content';

export type DxAnchorActivateProps = {
  eid: string;
  label: string;
  trigger: HTMLElement;
  kind?: 'base' | 'card';
  title?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  props?: Record<string, unknown>;
  /**
   * Set to `false` to close any open popover. When omitted (or `true`), the event opens a
   * popover anchored to `trigger`. `eid` / `label` / `trigger` may be placeholders when
   * `state` is `false` — the close path ignores them.
   */
  state?: boolean;
  /** Navigate straight to the anchor's target (e.g. a modifier-click) instead of opening its preview. */
  navigate?: boolean;
};

/**
 * Input to an anchor-preview lookup: the eid and display label from the clicked anchor.
 */
export type PreviewLinkRef = {
  eid: string;
  label: string;
  block?: boolean;
};

/**
 * Result of an anchor-preview lookup.
 */
export type PreviewLinkTarget = {
  label: string;
  object?: any;
};

/**
 * Global event to trigger a popover.
 */
export class DxAnchorActivate extends Event {
  public readonly eid: string;
  public readonly label: string;
  public readonly trigger: HTMLElement;
  public readonly kind?: 'base' | 'card';
  public readonly title?: string;
  public readonly side?: 'top' | 'right' | 'bottom' | 'left';
  public readonly props?: Record<string, unknown>;
  public readonly state?: boolean;
  public readonly navigate?: boolean;

  constructor(props: DxAnchorActivateProps) {
    super(DX_ANCHOR_ACTIVATE);
    this.eid = props.eid;
    this.label = props.label;
    this.trigger = props.trigger;
    this.kind = props.kind;
    this.title = props.title;
    this.side = props.side;
    this.props = props.props;
    this.state = props.state;
    this.navigate = props.navigate;
  }
}
